import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import api from '../utils/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, Building2, Mail, Globe, Key, Users, Settings, 
  CheckCircle, XCircle, Clock, Plus, Trash2, Copy, ExternalLink,
  RefreshCw, Loader2, AlertCircle, Server
} from 'lucide-react';

const PlatformAdmin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tenants');
  
  // Tenants
  const [tenants, setTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  
  // Platform Email Settings
  const [emailSettings, setEmailSettings] = useState({
    master_mailersend_api_key: '',
    inbound_webhook_url: '',
    inbound_enabled: false
  });
  const [savingEmail, setSavingEmail] = useState(false);
  
  // Tenant Domains (inbound email domains per tenant)
  const [tenantDomains, setTenantDomains] = useState([]);
  const [showAddDomainDialog, setShowAddDomainDialog] = useState(false);
  const [newDomain, setNewDomain] = useState({ domain: '', tenant_id: '', public_email: '' });
  const [addingDomain, setAddingDomain] = useState(false);
  const [editingDomainId, setEditingDomainId] = useState(null);
  const [editPublicEmail, setEditPublicEmail] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tenantsRes, settingsRes, domainsRes] = await Promise.all([
        api.get('/platform/tenants'),
        api.get('/platform/email-settings'),
        api.get('/platform/inbound-domains')
      ]);
      
      setTenants(tenantsRes.data.tenants || []);
      setEmailSettings(settingsRes.data || {});
      setTenantDomains(domainsRes.data.domains || []);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Platform Admin access required');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load platform data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEmailSettings = async () => {
    setSavingEmail(true);
    try {
      await api.put('/platform/email-settings', emailSettings);
      toast.success('Email settings saved successfully');
    } catch (error) {
      toast.error('Failed to save email settings');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleAddDomain = async () => {
    if (!newDomain.domain || !newDomain.tenant_id) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setAddingDomain(true);
    try {
      const response = await api.post('/platform/inbound-domains', newDomain);
      setTenantDomains([...tenantDomains, response.data.domain]);
      setShowAddDomainDialog(false);
      setNewDomain({ domain: '', tenant_id: '', public_email: '' });
      toast.success('Domain added successfully. DNS records are shown below.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add domain');
    } finally {
      setAddingDomain(false);
    }
  };

  const handleDeleteDomain = async (domainId) => {
    if (!confirm('Are you sure you want to remove this domain?')) return;
    
    try {
      await api.delete(`/platform/inbound-domains/${domainId}`);
      setTenantDomains(tenantDomains.filter(d => d.id !== domainId));
      toast.success('Domain removed');
    } catch (error) {
      toast.error('Failed to remove domain');
    }
  };

  const handleUpdatePublicEmail = async (domainId) => {
    try {
      await api.put(`/platform/inbound-domains/${domainId}`, { public_email: editPublicEmail });
      setTenantDomains(tenantDomains.map(d => 
        d.id === domainId ? { ...d, public_email: editPublicEmail } : d
      ));
      setEditingDomainId(null);
      setEditPublicEmail('');
      toast.success('Public email updated');
    } catch (error) {
      toast.error('Failed to update public email');
    }
  };

  const handleVerifyDomain = async (domainId) => {
    try {
      const response = await api.post(`/platform/inbound-domains/${domainId}/verify`);
      if (response.data.verified) {
        toast.success('Domain verified successfully!');
        // Update domain status
        setTenantDomains(tenantDomains.map(d => 
          d.id === domainId ? { ...d, verified: true } : d
        ));
      } else {
        toast.info(response.data.message || 'Domain not yet verified. Please check DNS records.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify domain');
    }
  };

  const handleSyncDomain = async (domainId) => {
    try {
      toast.info('Syncing domain with MailerSend...');
      const response = await api.post(`/platform/inbound-domains/${domainId}/sync-mailersend`);
      toast.success(response.data.message || 'Domain synced successfully!');
      // Refresh domains list
      const domainsRes = await api.get('/platform/inbound-domains');
      setTenantDomains(domainsRes.data.domains || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync domain with MailerSend');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getWebhookUrl = () => {
    const baseUrl = window.location.origin.replace(':3000', ':8001');
    return `${baseUrl}/api/webhook/mailersend/inbound`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-slate-100 p-4">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/dashboard')} className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Platform Administration</h1>
              <p className="text-gray-600 text-sm">Manage tenants, domains, and platform-wide settings</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 max-w-md">
            <TabsTrigger value="tenants" className="gap-2">
              <Building2 className="w-4 h-4" />
              Tenants
            </TabsTrigger>
            <TabsTrigger value="email" className="gap-2">
              <Mail className="w-4 h-4" />
              Email Setup
            </TabsTrigger>
            <TabsTrigger value="domains" className="gap-2">
              <Globe className="w-4 h-4" />
              Inbound Domains
            </TabsTrigger>
          </TabsList>

          {/* Tenants Tab */}
          <TabsContent value="tenants" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-purple-600" />
                  MSP Tenants
                </CardTitle>
                <CardDescription>
                  View and manage all MSP tenants on the platform
                </CardDescription>
              </CardHeader>
              <CardContent>
                {tenants.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No tenants found</p>
                    <p className="text-sm">Tenants are created automatically when users register with new email domains</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Domain</TableHead>
                        <TableHead>Admin</TableHead>
                        <TableHead>Users</TableHead>
                        <TableHead>Inbound Email</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tenants.map((tenant) => (
                        <TableRow key={tenant.domain}>
                          <TableCell className="font-medium">{tenant.domain}</TableCell>
                          <TableCell>
                            {tenant.admin_name || 'N/A'}
                            <br />
                            <span className="text-xs text-gray-500">{tenant.admin_email}</span>
                          </TableCell>
                          <TableCell>{tenant.user_count || 0}</TableCell>
                          <TableCell>
                            {tenant.inbound_domain ? (
                              <Badge variant="outline" className="bg-green-50 text-green-700">
                                {tenant.inbound_domain}
                              </Badge>
                            ) : (
                              <span className="text-gray-400">Not configured</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-gray-500">
                            {new Date(tenant.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-green-50 text-green-700">
                              Active
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email Setup Tab */}
          <TabsContent value="email" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server className="w-5 h-5 text-blue-600" />
                  Platform Email Configuration
                </CardTitle>
                <CardDescription>
                  Configure the master email service settings for all tenants
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Master API Key */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Key className="w-4 h-4 text-gray-500" />
                    Master API Key
                  </Label>
                  <Input
                    type="password"
                    placeholder="Enter your email service API key"
                    value={emailSettings.master_mailersend_api_key || ''}
                    onChange={(e) => setEmailSettings({
                      ...emailSettings,
                      master_mailersend_api_key: e.target.value
                    })}
                  />
                  <p className="text-xs text-gray-500">
                    This API key is used to manage inbound domains for all tenants
                  </p>
                </div>

                {/* Webhook URL */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-gray-500" />
                    Webhook URL
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={getWebhookUrl()}
                      readOnly
                      className="bg-gray-50 font-mono text-sm"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => copyToClipboard(getWebhookUrl())}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Use this URL when configuring inbound routing for each domain
                  </p>
                </div>

                {/* Enable/Disable */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <Label className="font-medium">Enable Email-to-Ticket</Label>
                    <p className="text-xs text-gray-500">
                      Allow tenants to receive tickets via email
                    </p>
                  </div>
                  <Switch
                    checked={emailSettings.inbound_enabled || false}
                    onCheckedChange={(checked) => setEmailSettings({
                      ...emailSettings,
                      inbound_enabled: checked
                    })}
                  />
                </div>

                <Button 
                  onClick={handleSaveEmailSettings}
                  disabled={savingEmail}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {savingEmail ? 'Saving...' : 'Save Email Settings'}
                </Button>
              </CardContent>
            </Card>

            {/* Setup Instructions */}
            <Card className="border-blue-200 bg-blue-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-blue-900 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  How Email-to-Ticket Works
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-blue-800 space-y-2">
                <p>1. <strong>You configure</strong> the master API key here (one-time setup)</p>
                <p>2. <strong>Tenants add</strong> their support domain in the &quot;Inbound Domains&quot; tab</p>
                <p>3. <strong>System shows</strong> DNS records for the tenant to add</p>
                <p>4. <strong>Once verified</strong>, emails to that domain create tickets automatically</p>
                <p>5. <strong>Tickets route</strong> to the correct tenant based on the recipient domain</p>
              </CardContent>
            </Card>

            {/* MailerSend Setup Instructions */}
            <Card className="border-purple-200 bg-purple-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-purple-900 text-sm flex items-center gap-2">
                  <ExternalLink className="w-4 h-4" />
                  MailerSend Inbound Routing Setup
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-purple-800 space-y-3">
                <p className="font-medium">Follow these steps in your MailerSend dashboard:</p>
                
                <div className="bg-white/50 rounded-lg p-3 space-y-2">
                  <p><strong>Step 1:</strong> Go to <a href="https://app.mailersend.com/domains" target="_blank" rel="noopener noreferrer" className="text-purple-600 underline">MailerSend Domains</a> and add your subdomain (e.g., <code className="bg-purple-100 px-1 rounded">support.yourdomain.com</code>)</p>
                  
                  <p><strong>Step 2:</strong> Add the DNS records shown (MX, SPF, DKIM) to your DNS provider</p>
                  
                  <p><strong>Step 3:</strong> Go to <a href="https://app.mailersend.com/inbound" target="_blank" rel="noopener noreferrer" className="text-purple-600 underline">Email → Inbound Routing</a></p>
                  
                  <p><strong>Step 4:</strong> Click <strong>&quot;Add inbound route&quot;</strong> and configure:</p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li>Select your domain</li>
                    <li>Set <strong>Catch filter</strong> to &quot;Catch all&quot;</li>
                    <li>Set <strong>Match filter</strong> to &quot;Match all&quot;</li>
                    <li>Under <strong>Forward to</strong>, select &quot;Webhook&quot;</li>
                    <li>Paste the <strong>Webhook URL</strong> from above</li>
                  </ul>
                  
                  <p><strong>Step 5:</strong> Save and test by sending an email to your support address</p>
                </div>

                <div className="bg-amber-100 border border-amber-300 rounded-lg p-3 mt-3">
                  <p className="font-medium text-amber-800 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Important: Using Main Domain Email
                  </p>
                  <p className="text-amber-700 mt-1">
                    If you want customers to email <code className="bg-amber-200 px-1 rounded">support@yourdomain.com</code> (main domain) instead of a subdomain:
                  </p>
                  <ol className="list-decimal list-inside ml-2 mt-2 space-y-1 text-amber-700">
                    <li>Keep your main domain MX pointing to your email provider (Office 365, Google, etc.)</li>
                    <li>Create a mailbox <code className="bg-amber-200 px-1 rounded">support@yourdomain.com</code></li>
                    <li>Set up a <strong>forwarding rule</strong> to redirect emails to your subdomain (e.g., <code className="bg-amber-200 px-1 rounded">support@help.yourdomain.com</code>)</li>
                    <li>The subdomain routes through MailerSend to create tickets</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Inbound Domains Tab */}
          <TabsContent value="domains" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="w-5 h-5 text-green-600" />
                      Tenant Inbound Domains
                    </CardTitle>
                    <CardDescription>
                      Manage email domains for each tenant&apos;s ticket intake
                    </CardDescription>
                  </div>
                  <Button onClick={() => setShowAddDomainDialog(true)} className="gap-2">
                    <Plus className="w-4 h-4" />
                    Add Domain
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {tenantDomains.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Mail className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No inbound domains configured</p>
                    <p className="text-sm">Add a domain to enable email-to-ticket for a tenant</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {tenantDomains.map((domain) => (
                      <div key={domain.id} className="border rounded-lg p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Globe className="w-5 h-5 text-gray-400" />
                            <div>
                              <p className="font-medium">{domain.domain}</p>
                              <p className="text-sm text-gray-500">
                                Tenant: {domain.tenant_domain || 'Unknown'}
                              </p>
                              {domain.mailersend_domain_id && (
                                <p className="text-xs text-green-600 flex items-center gap-1">
                                  <Server className="w-3 h-3" />
                                  Connected to MailerSend
                                </p>
                              )}
                              {domain.registration_error && !domain.mailersend_domain_id && (
                                <p className="text-xs text-amber-600 flex items-center gap-1">
                                  <AlertCircle className="w-3 h-3" />
                                  {domain.registration_error}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {domain.verified ? (
                              <Badge className="bg-green-100 text-green-800">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Verified
                              </Badge>
                            ) : (
                              <>
                                <Badge className="bg-yellow-100 text-yellow-800">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {domain.registration_status === 'api_key_missing' ? 'API Key Missing' : 'Pending DNS'}
                                </Badge>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleVerifyDomain(domain.id)}
                                >
                                  <RefreshCw className="w-3 h-3 mr-1" />
                                  Verify
                                </Button>
                              </>
                            )}
                            {!domain.mailersend_domain_id && emailSettings.master_mailersend_api_key && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-blue-600 hover:text-blue-700"
                                onClick={() => handleSyncDomain(domain.id)}
                              >
                                <Server className="w-3 h-3 mr-1" />
                                Sync
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteDomain(domain.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        {/* DNS Records */}
                        {!domain.verified && domain.dns_records && (
                          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                            <p className="text-sm font-medium text-gray-700">Required DNS Records:</p>
                            <div className="space-y-1 font-mono text-xs">
                              <div className="flex gap-2 items-start">
                                <span className="bg-gray-200 px-2 py-0.5 rounded">MX</span>
                                <div>
                                  <p>Host: <strong>{domain.dns_records.mx?.host || domain.domain}</strong></p>
                                  <p>Value: <strong>{domain.dns_records.mx?.value || 'inbound.mailersend.net'}</strong></p>
                                  <p>Priority: <strong>{domain.dns_records.mx?.priority || 10}</strong></p>
                                </div>
                              </div>
                              {domain.dns_records.txt && (
                                <div className="flex gap-2 items-start mt-2">
                                  <span className="bg-gray-200 px-2 py-0.5 rounded">TXT</span>
                                  <div className="break-all">
                                    <p>Host: <strong>{domain.dns_records.txt?.host || domain.domain}</strong></p>
                                    <p>Value: <strong>{typeof domain.dns_records.txt === 'string' ? domain.dns_records.txt : domain.dns_records.txt?.value}</strong></p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Public Email Configuration (for Office 365 forwarding) */}
                        <div className="bg-purple-50 rounded-lg p-3 space-y-2">
                          <p className="text-sm font-medium text-purple-800 flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            Public Email Address (for forwarding)
                          </p>
                          <p className="text-xs text-purple-600 mb-2">
                            If customers email a main domain address (e.g., support@bitzuk.com) that forwards to this inbound domain, enter it here so the system can match incoming emails.
                          </p>
                          {editingDomainId === domain.id ? (
                            <div className="flex gap-2">
                              <Input
                                type="email"
                                value={editPublicEmail}
                                onChange={(e) => setEditPublicEmail(e.target.value)}
                                placeholder="e.g., supportdesk@bitzuk.com"
                                className="flex-1 h-8 text-sm"
                              />
                              <Button size="sm" onClick={() => handleUpdatePublicEmail(domain.id)}>
                                Save
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => { setEditingDomainId(null); setEditPublicEmail(''); }}>
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              {domain.public_email ? (
                                <code className="bg-purple-100 px-2 py-1 rounded text-sm">{domain.public_email}</code>
                              ) : (
                                <span className="text-gray-400 text-sm italic">Not configured</span>
                              )}
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => { setEditingDomainId(domain.id); setEditPublicEmail(domain.public_email || ''); }}
                              >
                                {domain.public_email ? 'Edit' : 'Add'}
                              </Button>
                            </div>
                          )}
                        </div>

                        {/* MailerSend Webhook Setup */}
                        <div className="bg-blue-50 rounded-lg p-3 space-y-2">
                          <p className="text-sm font-medium text-blue-800">MailerSend Inbound Route Setup:</p>
                          <div className="text-xs text-blue-700 space-y-1">
                            <p>1. In MailerSend, go to <strong>Email → Inbound Routing</strong></p>
                            <p>2. Add route for <strong>{domain.domain}</strong> with &quot;Catch all&quot; filter</p>
                            <p>3. Set webhook URL to:</p>
                            <div className="flex gap-2 mt-1">
                              <code className="bg-blue-100 px-2 py-1 rounded text-xs flex-1 break-all">{getWebhookUrl()}</code>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7"
                                onClick={() => copyToClipboard(getWebhookUrl())}
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Customer Email Address */}
                        <div className="bg-green-50 rounded-lg p-3">
                          <p className="text-sm font-medium text-green-800">Customer Support Email:</p>
                          {domain.public_email ? (
                            <p className="text-xs text-green-700 mt-1">
                              Customers email: <strong className="font-mono">{domain.public_email}</strong> → forwards to → <strong className="font-mono">{domain.domain}</strong> → creates ticket
                            </p>
                          ) : (
                            <p className="text-xs text-green-700 mt-1">
                              Customers can email: <strong className="font-mono">support@{domain.domain}</strong> (or any address @{domain.domain})
                            </p>
                          )}
                          <p className="text-xs text-green-600 mt-1 italic">
                            Tip: To use main domain (e.g., support@{domain.tenant_domain}), configure the &quot;Public Email Address&quot; above.
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Add Domain Dialog */}
        <Dialog open={showAddDomainDialog} onOpenChange={setShowAddDomainDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Inbound Domain</DialogTitle>
              <DialogDescription>
                Configure an email domain for a tenant to receive support tickets
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Tenant</Label>
                <select
                  className="w-full border rounded-md p-2"
                  value={newDomain.tenant_id}
                  onChange={(e) => setNewDomain({ ...newDomain, tenant_id: e.target.value })}
                >
                  <option value="">Select a tenant...</option>
                  {tenants.map((tenant) => (
                    <option key={tenant.domain} value={tenant.domain}>
                      {tenant.domain} {tenant.admin_name ? `(${tenant.admin_name})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="space-y-2">
                <Label>Inbound Email Domain (Subdomain)</Label>
                <Input
                  placeholder="help.clientdomain.com"
                  value={newDomain.domain}
                  onChange={(e) => setNewDomain({ ...newDomain, domain: e.target.value })}
                />
                <p className="text-xs text-gray-500">
                  The subdomain that receives emails via MailerSend (e.g., supportdesk.bitzuk.com)
                </p>
              </div>

              <div className="space-y-2">
                <Label>Public Email Address (Optional)</Label>
                <Input
                  placeholder="support@clientdomain.com"
                  value={newDomain.public_email}
                  onChange={(e) => setNewDomain({ ...newDomain, public_email: e.target.value })}
                />
                <p className="text-xs text-gray-500">
                  The customer-facing email that forwards to the inbound domain (e.g., supportdesk@bitzuk.com → forwards to → supportdesk.bitzuk.com)
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDomainDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddDomain} disabled={addingDomain}>
                {addingDomain ? 'Adding...' : 'Add Domain'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default PlatformAdmin;
